import type { ShapeData } from '@/lib/shapes/types/shape-data'
import type { PathCommand } from '@/lib/elements/types/path-command'
import { shapeToPathCommands, svgPathToPathCommands } from '@/lib/elements/shape-path-commands'

export function quadraticPoint(
  p0: { x: number; y: number },
  c: { x: number; y: number },
  p1: { x: number; y: number },
  t: number
) {
  const mt = 1 - t

  return {
    x: mt * mt * p0.x + 2 * mt * t * c.x + t * t * p1.x,
    y: mt * mt * p0.y + 2 * mt * t * c.y + t * t * p1.y,
  }
}

export function cubicPoint(
  p0: { x: number; y: number },
  c1: { x: number; y: number },
  c2: { x: number; y: number },
  p1: { x: number; y: number },
  t: number
) {
  const mt = 1 - t
  const mt2 = mt * mt
  const t2 = t * t

  return {
    x: p0.x * mt2 * mt + 3 * c1.x * mt2 * t + 3 * c2.x * mt * t2 + p1.x * t2 * t,
    y: p0.y * mt2 * mt + 3 * c1.y * mt2 * t + 3 * c2.y * mt * t2 + p1.y * t2 * t,
  }
}

/** Exported for `lib/boundaries/text-boundary.ts`, which resolves commands in JS and passes them to
 *  the Go kernel (Go cannot regenerate a custom pen path or a raw SVG `path` string from
 *  type/width/height). Shared rather than reimplemented so the WASM and JS paths can't drift — note
 *  the `length > 0` test here, which `resolveLocalPathCommands` in shape-contour-wrap.ts
 *  deliberately does NOT have. */
export function buildShapePathCommands(shape: ShapeData): PathCommand[] {
  if (Array.isArray(shape.pathCommands) && shape.pathCommands.length > 0) return shape.pathCommands
  if (shape.type === 'path' && shape.path) return svgPathToPathCommands(shape.path)
  return shapeToPathCommands(shape.type, shape.width, shape.height)
}

export function flattenShapePath(shape: ShapeData): Array<{ x: number; y: number }> {
  const commands = buildShapePathCommands(shape)
  const points: Array<{ x: number; y: number }> = []
  let current: { x: number; y: number } | null = null

  for (const cmd of commands) {
    if ('moveTo' in cmd) {
      current = { x: cmd.moveTo.x, y: cmd.moveTo.y }
      points.push(current)
      continue
    }

    if ('lineTo' in cmd) {
      current = { x: cmd.lineTo.x, y: cmd.lineTo.y }
      points.push(current)
      continue
    }

    if (!current) continue

    const end = { x: cmd.curveTo.x, y: cmd.curveTo.y }
    const c1 = { x: cmd.curveTo.controlX, y: cmd.curveTo.controlY }
    const c2x = cmd.curveTo.controlX2 ?? cmd.curveTo.deltaX
    const c2y = cmd.curveTo.controlY2 ?? cmd.curveTo.deltaY
    const samples = 18

    for (let i = 1; i <= samples; i++) {
      const t = i / samples

      points.push(
        typeof c2x === 'number' && typeof c2y === 'number'
          ? cubicPoint(current, c1, { x: c2x, y: c2y }, end, t)
          : quadraticPoint(current, c1, end, t)
      )
    }

    current = end
  }

  if (points.length > 0) {
    const first = points[0]
    const last = points[points.length - 1]

    if (first.x !== last.x || first.y !== last.y) points.push({ ...first })
  }

  return points
}
